import React, { useEffect } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";

import { UnifiedDataTable } from "../../../layout/unified-data-table/ui/unified-data-table";
import type {
  UnifiedDataTableColumnRenderers,
  UnifiedTableRow,
} from "../../../shared/ui/data-table/model/data-table-contract";
import { ROUTES } from "../../../system/router/routes";
import { resultsRecord } from "../model/results";

import * as Styled from "./results.styled";

interface IPatientInfoOutletContext {
  setIsStretchContent: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Results = () => {
  const navigate = useNavigate();
  const { patientID } = useParams();
  const outletContext = useOutletContext<
    IPatientInfoOutletContext | undefined
  >();
  const setIsStretchContent = outletContext?.setIsStretchContent;
  const contentScrollerRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!setIsStretchContent) {
      return;
    }

    setIsStretchContent(true);

    return () => {
      setIsStretchContent(false);
    };
  }, [setIsStretchContent]);

  const columnRenderers = React.useMemo<UnifiedDataTableColumnRenderers>(
    () => ({
      resultDetails: () => (
        <Styled.RowIcon
          className="row-action-icon"
          aria-label="Detail"
          role="img"
          title="Detail"
        >
          <i className="pi pi-arrow-right" aria-hidden="true" />
        </Styled.RowIcon>
      ),
      wrappingCell: ({ value }) => (
        <Styled.WrappingCell>{String(value ?? "—")}</Styled.WrappingCell>
      ),
    }),
    [],
  );

  const handleRowClick = React.useCallback(
    (row: UnifiedTableRow) => {
      if (!patientID || typeof row.id !== "string") {
        return;
      }

      navigate(
        ROUTES.PATIENT_DETAILS_RESULT_DETAILS.replace(
          ":patientID",
          patientID,
        ).replace(":resultID", row.id),
      );
    },
    [navigate, patientID],
  );

  return (
    <Styled.Container>
      <Styled.Title>Results</Styled.Title>
      <UnifiedDataTable
        record={resultsRecord}
        columnRenderers={columnRenderers}
        onRowClick={handleRowClick}
        useSharedSectionNav
        scrollContainerRef={contentScrollerRef}
      />
    </Styled.Container>
  );
};
